import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTour } from "@reactour/tour";
import SideNav from "./SideNav";
import TopNav from "./TopNav";
import Footer from "./Footer";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
// import NewRonRequestBox from "../modals/NewRonRequestBox";
import ChatComponent from "../chatbot/ChatComponent";
import DemoMainPage from "../../data/walkthroughDemo/DemoMainPage";
import LottieAnimation from "../../animation/LottieAnimation";
import {
  handleMarkDemoShown,
  removeShowDemo,
  setPrevTourStep,
  setShowTourNav,
} from "../../store/actions/dataActions";
import SessionWillExpireBox from "../modals/SessionWillExpireBox";
import SessionForcedLogoutBox from "../modals/SessionForcedLogoutBox";
import { isPathProtected } from "../../functions/helperFunctions";

const SIDE_NAV_WIDTH = 280;

const LayoutRoot = styled("div")(
  ({ theme, openNav, pathname, forceHideSideNav }) => ({
    display: "flex",
    flex: "1 1 auto",
    maxWidth: "100%",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: forceHideSideNav
        ? 0
        : pathname.startsWith("/pp")
          ? 0 // No padding for PP pages as they handle their own spacing
          : ["/custom-documents/sign"].includes(pathname)
            ? !openNav
              ? SIDE_NAV_WIDTH // Add padding for "/document-uploader-test" when the sidebar is open
              : 0 // No padding when the sidebar is closed
            : SIDE_NAV_WIDTH, // Default padding for all other paths
    },
  }),
);

const LayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

export const Layout = (props) => {
  const { isOpen, setIsOpen, currentStep } = useTour();
  const theme = useTheme();
  const sessions = useSelector((state) => state.sessions);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const sessionForcedLogout = useSelector(
    (state) => state.sessions.sessionForcedLogout,
  );
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const demoShown = useSelector((state) => state.nocData?.demoShown);
  const showDemo = useSelector((state) => state.nocData?.showDemo);
  const showTourNav = useSelector((state) => state?.nocData?.showTourNav);
  const prevTourStep = useSelector((state) => state?.nocData?.prevTourStep);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const { children } = props;
  const [openNav, setOpenNav] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [willExpireSoon, setWillExpireSoon] = useState(false);
  const [forceHideSideNav, setForceHideSideNav] = useState(false);
  const [forceHideAppChrome, setForceHideAppChrome] = useState(false);
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);

  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const isProtectedPath = isPathProtected(pathname);
  const isSessionReady =
    !!currentSession && !!sessions?.[currentSession]?.isValidated;
  const isBootstrapping =
    isProtectedPath && !sessionForcedLogout && !isSessionReady;

  useEffect(() => {
    if (!isBootstrapping) {
      setBootstrapTimedOut(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setBootstrapTimedOut(true);
    }, 20000);

    return () => clearTimeout(timeoutId);
  }, [isBootstrapping]);

  useEffect(() => {
    if (isBootstrapping && bootstrapTimedOut) {
      navigate("/login");
    }
  }, [isBootstrapping, bootstrapTimedOut, navigate]);

  useEffect(() => {
    const checkWillExpire = () => {
      const isExpiring = sessionStorage.getItem("willExpire") === "true";
      setWillExpireSoon(isExpiring);
    };

    const handleExpireUpdate = () => {
      checkWillExpire();
    };

    // Add listener
    window.addEventListener("expireUpdate", handleExpireUpdate);

    // Initial check
    checkWillExpire();

    return () => {
      window.removeEventListener("expireUpdate", handleExpireUpdate);
    };
  }, []);

  useEffect(() => {
    const showNavList =
      currentSession === "subUserSession"
        ? [1, 8, 9, 11, 12, 13, 14, 15, 16, 17, 18, 19]
        : // : [1, 6, 13, 14, 15, 16, 17, 18, 19, 20, 22, 24, 26, 27, 28];
          [1, 6, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20, 22, 24, 26, 27, 28];

    if (
      prevTourStep !== currentStep &&
      showTourNav &&
      !showNavList.includes(currentStep)
    ) {
      dispatch(setShowTourNav(false));
      dispatch(setPrevTourStep(currentStep));
    }
  }, [showTourNav, currentStep, currentSession, dispatch, prevTourStep]);

  const handlePathnameChange = useCallback(() => {
    if (openNav) {
      setOpenNav(false);
    }
  }, [openNav]);

  useEffect(() => {
    if (isOpen && !lgUp) {
      setIsOpen(false);
    }
  }, [isOpen, lgUp, setIsOpen]);

  // Allow other parts of the app to request closing the side nav
  useEffect(() => {
    const handleCloseSideNav = () => setOpenNav(false);
    window.addEventListener("closeSideNav", handleCloseSideNav);
    return () => window.removeEventListener("closeSideNav", handleCloseSideNav);
  }, []);

  // Allow forcing the side nav hidden (e.g., when editing PDFs)
  useEffect(() => {
    const handleForceOn = () => setForceHideSideNav(true);
    const handleForceOff = () => setForceHideSideNav(false);
    window.addEventListener("forceHideSideNavOn", handleForceOn);
    window.addEventListener("forceHideSideNavOff", handleForceOff);
    return () => {
      window.removeEventListener("forceHideSideNavOn", handleForceOn);
      window.removeEventListener("forceHideSideNavOff", handleForceOff);
    };
  }, []);

  // Allow forcing TopNav/Footer hidden while editing
  useEffect(() => {
    const handleChromeOn = () => setForceHideAppChrome(true);
    const handleChromeOff = () => setForceHideAppChrome(false);
    window.addEventListener("forceHideAppChromeOn", handleChromeOn);
    window.addEventListener("forceHideAppChromeOff", handleChromeOff);
    return () => {
      window.removeEventListener("forceHideAppChromeOn", handleChromeOn);
      window.removeEventListener("forceHideAppChromeOff", handleChromeOff);
    };
  }, []);

  useEffect(() => {
    if (isOpen && !openNav && currentStep === 20) {
      setOpenNav(true);
    }
  }, [openNav, isOpen, currentStep]);

  useEffect(() => {
    const demoTimeout = setTimeout(() => {
      if (showDemo) {
        setTourOpen(true);
      }
      dispatch(removeShowDemo());
    }, 2000);

    return () => clearTimeout(demoTimeout);
  }, [showDemo, dispatch]);

  useEffect(() => {
    if (!demoShown) {
      if (currentSession === "userSession" && currentUser?.user?.firstLogin) {
        // setTourOpen(true);
        dispatch(handleMarkDemoShown());
      } else if (
        currentSession === "subUserSession" &&
        currentUser?.subuser?.firstLogin
      ) {
        // setTourOpen(true);
        dispatch(handleMarkDemoShown());
      }
    }
  }, [currentSession, currentUser, demoShown, dispatch]);

  useEffect(
    () => {
      // signOut();
      handlePathnameChange();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname],
  );

  useEffect(() => {
    if (["/custom-documents/sign"].includes(pathname)) {
      setTimeout(() => {
        setOpenNav(true);
      }, 3000);
    }
  }, [pathname]);

  const MemoizedFooter = useMemo(() => <Footer />, []);

  if (isBootstrapping && !bootstrapTimedOut) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <>
          <Box sx={{ mb: 2 }}>
            <img
              style={{
                width: "10rem",
                marginLeft: "auto",
                marginRight: "auto",
              }}
              alt="Permit Rockstar Logo"
              src="/img/website_img/PermitRockstar.png"
            />
          </Box>
          <LottieAnimation />
        </>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh", // Set the minimum height of the layout to the full viewport height
        }}
      >
        {currentSession && (
          <SessionWillExpireBox
            willExpireSoon={willExpireSoon}
            setWillExpireSoon={setWillExpireSoon}
          />
        )}
        {sessionForcedLogout && (
          <SessionForcedLogoutBox
            onNavigateToLogin={() => {
              navigate("/login");
            }}
          />
        )}

        {!forceHideAppChrome && (
          <Box
            component="header"
            sx={{
              backdropFilter: "blur(6px)",
              backgroundColor: (theme) =>
                alpha(theme.palette.background.default, 0.7),
              position: "sticky", // Make the header sticky
              top: 0, // Stick it at the top
              left: {
                lg: forceHideSideNav ? 0 : `${SIDE_NAV_WIDTH}px`,
              },
              width: {
                lg: forceHideSideNav
                  ? "100%"
                  : `calc(100% - ${SIDE_NAV_WIDTH}px)`,
              },
              zIndex: (theme) => theme.zIndex.appBar,
            }}
          >
            {/* {currentSession === "notarySession" && <NewRonRequestBox />} */}
            {!pathname.startsWith("/pp") ? (
              <TopNav
                onNavOpen={() => setOpenNav(true)}
                togleNav={() => setOpenNav(!openNav)}
                openNav={openNav}
                setTourOpen={setTourOpen}
              />
            ) : (
              // <PpTopNav />
              <></>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
          {!forceHideSideNav && (
            <SideNav
              onClose={() => setOpenNav(false)}
              open={openNav}
              SIDE_NAV_WIDTH={SIDE_NAV_WIDTH}
            />
          )}

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: { xs: "auto", md: "hidden" },
              backgroundColor: "#edf1f5",
            }}
          >
            <LayoutRoot
              openNav={openNav}
              pathname={pathname}
              forceHideSideNav={forceHideSideNav}
            >
              <LayoutContainer
                sx={{
                  maxWidth: "100%",
                  zIndex: 0,
                }}
              >
                {children}
              </LayoutContainer>
            </LayoutRoot>
            {currentUser?.role !== "notary" &&
            ![
              "/login",
              "/register",
              "/resetpassword",
              "/signee/manage",
              "/signee/auth-login",
              "/record/signee/register",
            ].includes(pathname) ? (
              <ChatComponent />
            ) : null}
          </Box>
        </Box>

        {currentSession !== "notarySession" &&
          !tabletSize &&
          !["/custom-documents/sign"].includes(pathname) &&
          !pathname.startsWith("/pp") && (
            <Box
              component="footer"
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "#1c2536",
                left: {
                  lg: forceHideSideNav ? 0 : `${SIDE_NAV_WIDTH}px`,
                },

                width: {
                  lg: forceHideSideNav
                    ? "100%"
                    : `calc(100% - ${SIDE_NAV_WIDTH}px)`,
                },
                zIndex: (theme) => theme.zIndex.appBar,
              }}
            >
              {MemoizedFooter}
            </Box>
          )}
        <DemoMainPage
          open={tourOpen}
          setOpen={setTourOpen}
          openNav={openNav}
          setOpenNav={setOpenNav}
        />
      </Box>
    </>
  );
};
